import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../api';

const STORAGE_KEY = 'qproject_app_bg_settings_v1';

const AppBackgroundContext = createContext(null);

export function AppBackgroundProvider({ children }) {
  const [activeImage, setActiveImage] = useState(null);
  const [availableImages, setAvailableImages] = useState([]);
  const [randomImage, setRandomImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return { enabled: true, mode: 'static' };
      const parsed = JSON.parse(raw);
      return {
        enabled: parsed?.enabled !== false,
        mode: parsed?.mode === 'random' ? 'random' : 'static',
        invertMainOrder: !!parsed?.invertMainOrder
      };
    } catch {
      return { enabled: true, mode: 'static', invertMainOrder: false };
    }
  });

  const refreshBackgrounds = useCallback(async () => {
    try {
      setLoading(true);
      const [activeRes, galleryRes] = await Promise.all([
        api.get('/profile/images/active'),
        api.get('/profile/images/gallery?category=profile&page=1&limit=100')
      ]);

      const nextActiveImage = activeRes?.data?.image?.imageData || null;
      const nextGallery = Array.isArray(galleryRes?.data?.images)
        ? galleryRes.data.images
            .map((img) => img?.imageData)
            .filter((url) => typeof url === 'string' && url.trim())
        : [];

      setActiveImage(nextActiveImage);
      setAvailableImages(nextGallery);
    } catch (err) {
      console.warn('No se pudieron cargar los fondos globales:', err);
      setActiveImage(null);
      setAvailableImages([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshBackgrounds();
  }, [refreshBackgrounds]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  useEffect(() => {
    if (!(settings.enabled && settings.mode === 'random' && availableImages.length > 1)) {
      setRandomImage(null);
      return undefined;
    }

    const pickRandom = () => {
      setRandomImage((previous) => {
        const pool = availableImages.filter((url) => url !== previous);
        if (pool.length === 0) return previous || availableImages[0];
        const randomIdx = Math.floor(Math.random() * pool.length);
        return pool[randomIdx];
      });
    };

    pickRandom();
    const intervalId = window.setInterval(pickRandom, 60 * 1000);

    return () => window.clearInterval(intervalId);
  }, [settings.enabled, settings.mode, availableImages]);

  const cycleHeaderMode = useCallback(() => {
    setSettings((prev) => {
      if (!prev.enabled) return { ...prev, enabled: true, mode: 'static' };
      if (prev.mode === 'static') return { ...prev, enabled: true, mode: 'random' };
      return { ...prev, enabled: false, mode: 'static' };
    });
  }, []);

  const toggleInvertMainOrder = useCallback(() => {
    setSettings((prev) => ({
      ...prev,
      invertMainOrder: !prev.invertMainOrder
    }));
  }, []);

  const hasBackground = Boolean(activeImage || availableImages.length > 0);

  const displayedImage = useMemo(() => {
    if (!hasBackground) return null;
    if (settings.mode === 'random' && settings.enabled) {
      return randomImage || availableImages[0] || activeImage;
    }
    return activeImage || availableImages[0] || null;
  }, [hasBackground, settings.mode, settings.enabled, randomImage, availableImages, activeImage]);

  const value = useMemo(() => ({
    activeImage,
    availableImages,
    hasBackground,
    loading,
    settings,
    displayedImage,
    cycleHeaderMode,
    toggleInvertMainOrder,
    refreshBackgrounds
  }), [activeImage, availableImages, hasBackground, loading, settings, displayedImage, cycleHeaderMode, toggleInvertMainOrder, refreshBackgrounds]);

  return (
    <AppBackgroundContext.Provider value={value}>
      {children}
    </AppBackgroundContext.Provider>
  );
}

export function useAppBackground() {
  return useContext(AppBackgroundContext);
}

